import { Feature, GeoJsonProperties, Polygon } from "geojson";
import { FitBoundsOptions } from "mapbox-gl";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Layer, Marker, Source, useMap } from "react-map-gl";
import { useDriveTimeStore } from "../..";
import GeometryService from "../../services/geometryService";

const driveTimeFillLayerId = "driveTimeFillLayer";
const driveTimeBorderLayerId = "driveTimeBorderLayer";

function DriveTimeMapLayer() {
    const map = useMap().current!;
    const driveTimeStore = useDriveTimeStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const [feature, setFeature] = useState<Feature<Polygon, GeoJsonProperties>>();

    const driveTimePolygonGeoJson: any = useMemo(() => ({
        type: "FeatureCollection",
        features: feature ? [feature] : [],
    }), [feature]);

    useEffect(() => {
        let isRequestCancelled = false;
        if (driveTimeStore.coordinates) {
            const coordinates = driveTimeStore.coordinates;
            const minutes = driveTimeStore.minutes;
            driveTimeStore.setIsLoading(true);
            driveTimeStore.fetchIsochroneShape(coordinates, minutes)
                .then(shape => {
                    if (!isRequestCancelled) {
                        setFeature(shape);
                        map.fitBounds(geometryService.getBoundaryBox(shape), fitBoundsOptions);
                        if (driveTimeStore.isIndustrialScarcitySupported)
                            driveTimeStore.fetchIndustrialScarcityPercent(coordinates, minutes);
                    }
                })
                .finally(() => !isRequestCancelled && driveTimeStore.setIsLoading(false));
        } else
            setFeature(undefined);

        return () => {
            isRequestCancelled = true;
        };
    }, [driveTimeStore.coordinates, driveTimeStore.minutes]);

    if (!driveTimeStore.coordinates || !feature)
        return null;

    return (
        <Source id="driveTimeSource" type="geojson" data={driveTimePolygonGeoJson}>
            <Marker
                latitude={driveTimeStore.coordinates!.latitude}
                longitude={driveTimeStore.coordinates!.longitude}
                draggable={false}
                color="#105491"
            />
            <Layer
                id={driveTimeFillLayerId}
                type="fill"
                paint={{
                    "fill-antialias": false,
                    "fill-color": "#105491",
                    "fill-opacity": 0.3,
                }}
            />
            <Layer
                id={driveTimeBorderLayerId}
                type="line"
                layout={{
                    "line-cap": "round",
                    "line-join": "round",
                }}
                paint={{
                    "line-color": "#105491",
                    "line-width": 5,
                }}
            />
        </Source>
    );
}

const fitBoundsOptions: FitBoundsOptions = {
    padding: {
        left: 520,
        top: 300,
        right: 500,
        bottom: 300,
    },
    animate: true,
};

export default observer(DriveTimeMapLayer);